import type {
	ParameterizedConceptDTORelativeness,
	ParameterizedConceptDTORelativenessModeEnum,
	ParameterizedConceptDTORepresentationModeEnum,
} from "@eu.mdotm.api/smart-workspaces";
import type { ConceptParameterForm } from "./chart-type";

/**
 * The representation-mode rules the chain, the persisted concept and the generated name share.
 *
 * `BENCHMARK_VALUE` is the reference object's own value (`ABSOLUTE − RELATIVE` for the same reference). Like
 * `RELATIVE` it is qualified by a relativeness; unlike it, the chain only offers benchmarks as that reference.
 */

/** Whether a concept in this mode is computed against a reference, and so carries a relativeness. */
export function takesRelativeness(mode: ParameterizedConceptDTORepresentationModeEnum | undefined | null): boolean {
	return mode === "RELATIVE" || mode === "BENCHMARK_VALUE";
}

/** The kinds of reference the picker offers for this mode, out of the ones the concept declares. */
export function relativenessModesFor(
	mode: ParameterizedConceptDTORepresentationModeEnum | undefined | null,
	conceptRelativenessModes: ParameterizedConceptDTORelativenessModeEnum[] | undefined,
): ParameterizedConceptDTORelativenessModeEnum[] {
	if (mode === "BENCHMARK_VALUE") {
		return (conceptRelativenessModes ?? []).filter((relativenessMode) => relativenessMode === "BENCHMARK");
	}
	return conceptRelativenessModes ?? [];
}

/** Whether a chain left without a reference adopts each portfolio's own one, rather than the first one listed. */
export function defaultsToOwnReference(
	mode: ParameterizedConceptDTORepresentationModeEnum | undefined | null,
): boolean {
	return mode === "BENCHMARK_VALUE";
}

/** The chain's reference after switching to `mode`: kept when the mode still offers its kind, dropped otherwise. */
export function relativenessKeptFor(
	mode: ParameterizedConceptDTORepresentationModeEnum,
	relativeness: ParameterizedConceptDTORelativeness | undefined,
	conceptRelativenessModes: ParameterizedConceptDTORelativenessModeEnum[] | undefined,
): ParameterizedConceptDTORelativeness | undefined {
	return relativeness && relativenessModesFor(mode, conceptRelativenessModes).includes(relativeness.mode)
		? relativeness
		: undefined;
}

/** The concept's representation modes, less `BENCHMARK_VALUE` when it has no benchmark to be the value of. */
export function offeredRepresentationModes(
	form: Pick<ConceptParameterForm, "representationModes" | "relativenessModes">,
): ParameterizedConceptDTORepresentationModeEnum[] {
	return (form.representationModes ?? []).filter(
		(mode) => mode !== "BENCHMARK_VALUE" || relativenessModesFor(mode, form.relativenessModes).length > 0,
	);
}

/** The generated name's word between the metric and its reference. */
export function relativenessWord(mode: ParameterizedConceptDTORepresentationModeEnum | undefined | null): string {
	return mode === "BENCHMARK_VALUE" ? "Bmk value of" : "Rel to";
}
